import { useEffect, useState, useCallback } from 'react'
import { withRouter } from 'react-router'
import classNames from 'classnames'
import { Button, Tag } from 'baby-design'
import DrawerModal from 'components/modal/drawer-modal'
import { Star, Close } from 'baby-design/icons'
import { track, useTracking } from 'lib/analytics'
import { FacetPanel } from '../FacetPanel'
import { Facet } from '../FacetControl/Types'
import {
  constructFilterQueryString,
  parseFilterQueryParams,
} from '../../../../lib'
import { filterEmptyFilters } from '../../../product-filters/helpers'
import { useCollectionFacets } from '../../../../api/hooks'
import css from './FacetsBar.styles.scss'

export interface SelectedFilters {
  [key: string]: string[] | undefined
}

interface FacetsBarProps {
  slug: string
  facetsType: 'category' | 'brand'
  facets: Facet<any>[]
  router: any
  selectedFilters: SelectedFilters
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>
}

interface FilterChipProps {
  filterKey: string
  values: string | string[]
  facets: Facet<any>[]
  onRemove: (key: string, value?: string) => void
}

interface CatalogCategoryFacetsResponse {
  data: {
    facets: Facet<any>[]
  }
  isLoading: boolean
}

const PriceFilterChip: React.FC<FilterChipProps> = ({
  filterKey,
  values,
  onRemove,
}) => {
  if (!Array.isArray(values) || values.length !== 2) return null

  return (
    <Tag
      RightIcon={<Close />}
      key={`${filterKey}-range`}
      size="lg"
      type="primary"
      variant="normal"
      onClick={() => onRemove(filterKey)}
    >
      {`$${values[0]}-$${values[1]}`}
    </Tag>
  )
}

const RatingFilterChip: React.FC<FilterChipProps> = ({
  filterKey,
  values,
  onRemove,
}) => {
  if (!Array.isArray(values) || values.length === 0) return null

  return (
    <Tag
      LeftIcon={<Star filled="full" />}
      RightIcon={<Close />}
      key={`${filterKey}-range`}
      size="lg"
      type="primary"
      variant="normal"
      onClick={() => onRemove(filterKey)}
    >
      {`${Math.min(...values.map(Number))} & up`}
    </Tag>
  )
}

const DefaultFilterChip: React.FC<FilterChipProps> = ({
  filterKey,
  values,
  facets,
  onRemove,
}) => {
  const renderValue = (value: string): string => {
    const facet = facets.find((f) => f.key === filterKey)
    if (facet?.type === 'boolean') {
      return facet.label
    }

    switch (filterKey) {
      case 'inventory_available':
        return 'In Stock'
      case 'on_sale':
        return 'On Sale'
      case 'fsa_hsa_eligible':
        return 'FSA & HSA Eligible'
      default:
        return value
    }
  }

  const valueArray = Array.isArray(values) ? values : [values]

  return (
    <>
      {valueArray.map((value) => (
        <Tag
          RightIcon={<Close />}
          key={`${filterKey}-${value}`}
          size="lg"
          type="primary"
          variant="normal"
          onClick={() => onRemove(filterKey, value)}
        >
          {renderValue(value)}
        </Tag>
      ))}
    </>
  )
}

const FILTER_COMPONENTS: Record<string, React.FC<FilterChipProps>> = {
  price: PriceFilterChip,
  user_rating: RatingFilterChip,
}

const FilterChips: React.FC<{
  selectedFilters: SelectedFilters
  facets: Facet<any>[]
  onRemoveFilter: (key: string, value?: string) => void
  onClearFilters: () => void
}> = ({ selectedFilters, facets, onRemoveFilter, onClearFilters }) => {
  if (Object.keys(selectedFilters).length === 0) return null

  return (
    <div className={css.FacetsBar__chips_container}>
      <div className={css.FacetsBar__chips}>
        {Object.entries(selectedFilters).map(([key, values]) => {
          if (!values) return null

          const FilterComponent = FILTER_COMPONENTS[key] || DefaultFilterChip
          return (
            <FilterComponent
              facets={facets}
              filterKey={key}
              key={key}
              values={values}
              onRemove={onRemoveFilter}
            />
          )
        })}
        <button
          className={css.FacetsBar__clear_link}
          type="button"
          onClick={onClearFilters}
        >
          Clear filters
        </button>
      </div>
    </div>
  )
}

const FacetsBar: React.FC<FacetsBarProps> = ({
  slug,
  router,
  selectedFilters,
  setSelectedFilters,
  facetsType = 'category',
}) => {
  const tracker = useTracking()
  const [showModal, setShowModal] = useState(false)
  const [initialOpenSection, setInitialOpenSection] = useState('')
  const [originalFilters, setOriginalFilters] = useState<SelectedFilters>({})
  const [selectedSort, setSelectedSort] = useState(
    new URLSearchParams(router.location.search).get('sort') || ''
  )

  useEffect(() => {
    const newFilters = parseFilterQueryParams(
      router.location.search
    ) as SelectedFilters
    setSelectedFilters(newFilters)
  }, [router.location.search, setSelectedFilters])

  const updateUrlWithFilters = useCallback(
    (filters: SelectedFilters) => {
      const nonEmptyFilters = filterEmptyFilters(filters)
      const filterQueryString = constructFilterQueryString(nonEmptyFilters)
      const sortQueryString = selectedSort ? `sort=${selectedSort}` : ''
      const queryString = [filterQueryString, sortQueryString]
        .filter(Boolean)
        .join('&')
      router.push(`${window.location.pathname}?${queryString}`)
    },
    [selectedSort, router]
  )

  const closePanel = useCallback(
    (revertChanges = false) => {
      if (revertChanges) {
        setSelectedFilters(originalFilters)
      }
      setShowModal(false)
    },
    [originalFilters, setSelectedFilters]
  )

  // @ts-ignore
  const { data, isLoading } = useCollectionFacets(
    facetsType,
    slug,
    selectedFilters
  ) as CatalogCategoryFacetsResponse

  const facetsToDisplay = isLoading
    ? []
    : data.facets.filter((facet: Facet<any>) => facet.key !== 'featured_brands')

  const handleRemoveFilter = useCallback(
    (key: string, value?: string) => {
      setSelectedFilters((prevFilters) => {
        const newFilters = { ...prevFilters }
        if (value && Array.isArray(newFilters[key])) {
          newFilters[key] = newFilters[key]?.filter((v) => v !== value)
          if (newFilters[key]?.length === 0) {
            delete newFilters[key]
          }
        } else {
          delete newFilters[key]
        }

        updateUrlWithFilters(newFilters)
        return newFilters
      })
    },
    [updateUrlWithFilters, setSelectedFilters]
  )

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({})
    setSelectedSort('')
    router.push(`${window.location.pathname}`)
    closePanel()
  }, [router, closePanel, setSelectedFilters])

  const handleFilterChange = useCallback(
    (facetKey: string, selectedOptions: any) => {
      setSelectedFilters((prevFilters) => {
        const newFilters = { ...prevFilters }
        if (selectedOptions.length === 0) {
          delete newFilters[facetKey]
        } else {
          newFilters[facetKey] = selectedOptions
        }
        return newFilters
      })
    },
    [setSelectedFilters]
  )

  const handleSortChange = useCallback((sortValue: string) => {
    setSelectedSort(sortValue)
  }, [])

  const openPanel = useCallback(
    (focusedSection?: string) => {
      setShowModal(true)
      setOriginalFilters(selectedFilters)
      setInitialOpenSection(focusedSection || '')

      tracker.trackEvent({
        event: track.filtersPanelToggled,
        focusedSection,
        isOpen: true,
        storeCategorySlug: window?.location?.pathname?.split('/')?.pop(),
      })
    },
    [selectedFilters, tracker]
  )

  const handleBooleanFilter = useCallback(
    (key: string) => {
      setSelectedFilters((prevFilters) => {
        const newFilters = { ...prevFilters }
        if (newFilters[key]) {
          delete newFilters[key]
        } else {
          newFilters[key] = ['true']
        }
        updateUrlWithFilters(newFilters)
        return newFilters
      })
    },
    [setSelectedFilters, updateUrlWithFilters]
  )

  const TriggerButtons = () => (
    <div className={css.FacetsBar__buttons}>
      <Button
        className={
          selectedSort !== '' ? css['FacetsBar__button--selected'] : undefined
        }
        size="sm"
        variant={selectedSort !== '' ? 'secondary' : 'tertiary'}
        onClick={() => openPanel('sort')}
      >
        Sort
      </Button>
      {facetsToDisplay.map((facet: Facet<any>) => {
        const isSelected = (selectedFilters[facet.key]?.length ?? 0) > 0
        const isBooleanFilter = facet.type === 'boolean'

        return (
          <Button
            className={classNames(
              'hidden-xs',
              isSelected && css['FacetsBar__button--selected']
            )}
            key={facet.key}
            size="sm"
            variant={isSelected ? 'secondary' : 'tertiary'}
            onClick={() =>
              isBooleanFilter
                ? handleBooleanFilter(facet.key)
                : openPanel(facet.key)
            }
          >
            {facet.label}
          </Button>
        )
      })}
      <Button
        className={classNames(
          'visible-xs',
          Object.keys(selectedFilters).length > 0 &&
            css['FacetsBar__button--selected']
        )}
        size="sm"
        variant={
          Object.keys(selectedFilters).length > 0 ? 'secondary' : 'tertiary'
        }
        onClick={() => openPanel()}
      >
        Filter
      </Button>
      <Button
        className={classNames(
          'visible-xs',
          selectedFilters.inventory_available &&
            css['FacetsBar__button--selected']
        )}
        size="sm"
        variant={selectedFilters.inventory_available ? 'secondary' : 'tertiary'}
        onClick={() => handleBooleanFilter('inventory_available')}
      >
        In Stock
      </Button>
    </div>
  )

  return (
    <DrawerModal
      origin="left"
      show={showModal}
      style={undefined}
      trigger={
        <div className={css.FacetsBar}>
          <TriggerButtons />
          <FilterChips
            facets={facetsToDisplay}
            selectedFilters={selectedFilters}
            onClearFilters={handleClearFilters}
            onRemoveFilter={handleRemoveFilter}
          />
        </div>
      }
      onHide={() => closePanel(true)}
    >
      <FacetPanel
        closePanel={closePanel}
        facets={facetsToDisplay}
        initialOpenSection={initialOpenSection}
        selectedFilters={selectedFilters}
        selectedSort={selectedSort}
        onApplyFilters={() => {
          updateUrlWithFilters(selectedFilters)
          closePanel()
        }}
        onClearFilters={handleClearFilters}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
      />
    </DrawerModal>
  )
}

export default withRouter(FacetsBar)
